import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';

import { NavigationData } from '../../../_shared/data/navigation.data';
import { ILanguageGroup } from '../../../_shared/models/language-picker/language-group.model';
import { ILanguageLink } from '../../../_shared/models/language-picker/language-link.model';
import { ILink } from '../../../_shared/models/link.model';
import { IMenuGroup } from '../../../_shared/models/navigation/menu/menu-group.model';
import { ISubMenuHighlight } from '../../../_shared/models/navigation/menu/sub-menu-highlight.model';
import { ISubMenuItem } from '../../../_shared/models/navigation/menu/sub-menu-item.model';
import { INavigationHub, IUtilityTab } from '../../../_shared/models/navigation/navigation-hub.model';
import { SidebarComponent } from '../../../sidebar/sidebar.component';
import { UtilityBarService } from '../../navigation-utility-bar/utility-bar.service';

@Component({
    selector: 'sirius-navigation-menu-mobile',
    templateUrl: './navigation-menu-mobile.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SidebarComponent],
})
export class NavigationMenuMobileComponent implements OnInit {
    private readonly _utilBarService = inject(UtilityBarService);

    readonly content = input<IMenuGroup[]>([]);

    readonly menuClose = output<void>();

    rootItem: ISubMenuItem = {
        submenuTitle: 'Menu',
        submenuItems: [],
    };

    private _navigationHub!: INavigationHub;

    ngOnInit(): void {
        const tabs = this._utilBarService.navigationTabs;
        this._navigationHub = {
            activeTab: this._utilBarService.activeTab,
            tabs: tabs.length ? tabs : NavigationData.tabs,
        };

        this.addPages();
        this.addGlobalLinks();
        this.addLanguages();
        this.addHub();
    }

    private addPages(): void {
        this.content().forEach((x: IMenuGroup) => {
            const child: ISubMenuItem = {
                submenuTitle: x.title,
                submenuLink: x.link,
                submenuParent: this.rootItem,
                submenuItems: [],
            };

            child.submenuItems = x.columns?.reduce((accumulator, value) => accumulator.concat(value), []);

            x.highlightItems?.forEach((y: ISubMenuHighlight) => {
                child.submenuItems?.push({
                    submenuTitle: y.highlightItemTitle,
                    submenuLink: y.highlightItemLink,
                    submenuParent: child,
                    submenuItems: [],
                });
            });

            this.rootItem.submenuItems?.push(child);
        });

        this.rootItem.submenuItems?.push({
            submenuTitle: '',
            submenuItems: [],
        });
    }

    private addLanguages(): void {
        if (!this._utilBarService.languages) return;

        const activeCountry = this._utilBarService.languages.selected.shortName;

        const langs: ISubMenuItem = {
            submenuTitle: `Languages`,
            submenuParent: this.rootItem,
            submenuItems: [],
        };

        this._utilBarService.languages.groups.forEach((x: ILanguageGroup) => {
            const region: ISubMenuItem = {
                submenuTitle: x.name,
                submenuParent: langs,
                submenuItems: [],
            };

            langs.submenuItems?.push(region);

            x.links.forEach((y: ILanguageLink) => {
                const country: ISubMenuItem = {
                    submenuTitle: y.country,
                    submenuSubtitle: y.link.title,
                    submenuLink: { ...y.link, noRouting: true },
                    submenuParent: region,
                    submenuItems: [],
                    submenuIcon: y.flag,
                    submenuActive: activeCountry === y.shortName,
                };

                if (activeCountry === y.shortName) {
                    langs.submenuTitle = y.country;
                    langs.submenuSubtitle = y.link.title;
                    langs.submenuLink = { ...y.link, noRouting: true };
                    langs.submenuIcon = y.flag;
                }

                region.submenuItems?.push(country);
            });
        });

        this.rootItem.submenuItems?.push(langs);
    }

    private addGlobalLinks(): void {
        this._utilBarService.globalLinks.forEach((x: ILink) =>
            this.rootItem.submenuItems?.push({
                submenuTitle: x.title,
                submenuLink: x,
                submenuItems: [],
                submenuParent: this.rootItem,
            })
        );
    }

    private addHub(): void {
        if (!this._navigationHub.tabs.length) return;

        const hub: ISubMenuItem = {
            submenuTitle: 'UR Hub',
            submenuItems: [],
            submenuIcon: 'squares_four',
            submenuParent: this.rootItem,
        };

        hub.submenuItems = this._navigationHub.tabs.map((x: IUtilityTab) => {
            return {
                submenuTitle: x.link.title,
                submenuItems: [],
                submenuParent: hub,
                submenuLink: { ...x.link, noRouting: true },
            };
        });

        this.rootItem.submenuItems?.push(hub);
    }
}
